import { useEffect, useMemo, useState } from "react";
import { FileText, X } from "lucide-react";
import { ApiError } from "@/shared/lib/apiClient";
import { cn } from "@/shared/lib/cn";
import type { ProformaApi } from "../api/proformaApi";
import {
  approveAuditorProforma,
  downloadProformaPdf,
  openProformaPdf,
  rejectAuditorProforma,
} from "../api/proformaApi";
import { StatusBadge } from "./StatusBadge";

type ProformaDetailModalProps = {
  proformaId: number;
  fallbackTitle: string;
  canFetch: boolean;
  fetcher: (id: number) => Promise<ProformaApi>;
  onClose: () => void;
  allowValidation?: boolean;
  onValidated?: () => void;
};

const statusLabels: Record<string, string> = {
  pending: "En attente",
  approved: "Approuvée",
  rejected: "Rejetée",
};

const urgencyLabels: Record<string, string> = {
  normal: "Normale",
  urgent: "Urgente",
  emergency: "Urgence vitale",
};

function formatAmount(value: number | undefined | null): string {
  const amount = Number(value ?? 0);
  return `$${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</span>
      <span className="text-sm text-foreground">{value || "—"}</span>
    </div>
  );
}

export function ProformaDetailModal({
  proformaId,
  fallbackTitle,
  canFetch,
  fetcher,
  onClose,
  allowValidation = false,
  onValidated,
}: ProformaDetailModalProps) {
  const [data, setData] = useState<ProformaApi | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [validationNotes, setValidationNotes] = useState("");
  const [actDrafts, setActDrafts] = useState<
    Record<number, { quantity: string; unit_price: string; notes: string }>
  >({});

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  useEffect(() => {
    if (!canFetch) {
      setError("Le détail complet nécessite une connexion à l'API.");
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);
    fetcher(proformaId)
      .then((row) => {
        if (active) {
          setData(row);
          const drafts: Record<number, { quantity: string; unit_price: string; notes: string }> =
            {};
          for (const act of row.request_acts ?? []) {
            drafts[act.id] = {
              quantity: String(act.approved_quantity ?? act.quantity ?? 0),
              unit_price: String(act.approved_unit_price ?? act.unit_price ?? 0),
              notes: act.auditor_note ?? "",
            };
          }
          setActDrafts(drafts);
        }
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Impossible de charger le détail.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [proformaId, canFetch, fetcher]);

  const status = String(data?.status ?? "pending");
  const urgency = String(data?.urgency_raw ?? data?.urgency ?? "normal");
  const coveredActs = data?.covered_acts ?? [];
  const requestActs = data?.request_acts ?? [];
  const hasOriginalPdf = Boolean(data?.proforma_pdf);
  const hasSignedPdf = Boolean(data?.proforma_pdf_signed);
  const canValidate = allowValidation && status === "pending" && canFetch;
  const isDependentPatient =
    data?.patient_type === "dependent" && Boolean(data?.dependent_name?.trim());
  const titulaireLabel = data?.employee_name || data?.patient;
  const titulaireId = data?.employee_id_label || data?.patient_id;

  const approvedTotal = useMemo(() => {
    return requestActs.reduce((sum, act) => {
      const draft = actDrafts[act.id];
      const qty = Number(draft?.quantity ?? act.quantity ?? 0);
      const price = Number(draft?.unit_price ?? act.unit_price ?? 0);
      return sum + qty * price;
    }, 0);
  }, [requestActs, actDrafts]);

  const handleOpenPdf = async (signed: boolean) => {
    if (!data) return;
    setPdfBusy(true);
    setError(null);
    try {
      await openProformaPdf(data.id, signed);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : signed ? "PDF signé indisponible." : "PDF indisponible.",
      );
    } finally {
      setPdfBusy(false);
    }
  };

  const handleDownloadPdf = async (signed: boolean) => {
    if (!data) return;
    setPdfBusy(true);
    setError(null);
    try {
      await downloadProformaPdf(
        data.id,
        signed,
        signed ? data.proforma_pdf_signed : data.proforma_pdf,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : signed ? "Téléchargement signé impossible." : "Téléchargement impossible.",
      );
    } finally {
      setPdfBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!data) return;
    setActionBusy(true);
    setError(null);
    try {
      await approveAuditorProforma(data.id, {
        notes: validationNotes.trim() || undefined,
        montant_approuve: approvedTotal > 0 ? approvedTotal : undefined,
        acts: requestActs.map((act) => ({
          id: act.id,
          quantity: Number(actDrafts[act.id]?.quantity ?? act.quantity ?? 0),
          unit_price: Number(actDrafts[act.id]?.unit_price ?? act.unit_price ?? 0),
          notes: actDrafts[act.id]?.notes ?? "",
        })),
      });
      onValidated?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Approbation impossible.",
      );
    } finally {
      setActionBusy(false);
    }
  };

  const handleReject = async () => {
    if (!data) return;
    const reason = window.prompt("Raison du rejet (obligatoire) :");
    if (!reason?.trim()) return;
    setActionBusy(true);
    setError(null);
    try {
      await rejectAuditorProforma(data.id, reason.trim(), validationNotes.trim() || undefined);
      onValidated?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Rejet impossible.",
      );
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Fermer"
        className="absolute inset-0 bg-black/45"
        onClick={onClose}
      />

      <div className="card-shadow relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-foreground">
              {data?.request_number || fallbackTitle}
            </h2>
            <StatusBadge
              variant={
                status === "approved" ? "success" : status === "rejected" ? "danger" : "warning"
              }
            >
              {statusLabels[status] ?? status}
            </StatusBadge>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card-muted hover:text-foreground"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto px-5 py-4">
          {loading && (
            <p className="py-12 text-center text-sm text-muted">Chargement du détail…</p>
          )}

          {error && !loading && (
            <div className="rounded-xl border border-danger/20 bg-danger-soft px-4 py-3">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {data && !loading && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoRow
                  label="Patient"
                  value={
                    <>
                      {isDependentPatient ? data?.dependent_name : data?.patient}
                      {titulaireId ? (
                        <span className="text-muted"> · {titulaireId}</span>
                      ) : null}
                      {isDependentPatient && titulaireLabel ? (
                        <span className="block text-xs text-muted">
                          Titulaire : {titulaireLabel}
                          {titulaireId ? ` (${titulaireId})` : ""}
                        </span>
                      ) : null}
                    </>
                  }
                />
                <InfoRow label="Forfait" value={`${data.package_code} / ${data.package_ref}`} />
                <InfoRow label="Hôpital" value={data.hospital} />
                <InfoRow label="Entreprise" value={data.company} />
                <InfoRow label="Montant estimé" value={formatAmount(data.estimated_amount)} />
                <InfoRow label="Urgence" value={urgencyLabels[urgency] ?? urgency} />
                <InfoRow label="Soumise le" value={data.date} />
                <InfoRow
                  label="Décidée le"
                  value={data.validated_at ? data.validated_at.slice(0, 10) : "—"}
                />
              </div>

              {(data.hospital_notes || data.validation_notes) && (
                <div className="space-y-3">
                  {data.hospital_notes && (
                    <div className="rounded-xl border border-border bg-card-muted/40 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Notes hôpital
                      </p>
                      <p className="mt-1 text-sm text-foreground">{data.hospital_notes}</p>
                    </div>
                  )}
                  {data.validation_notes && (
                    <div
                      className={cn(
                        "rounded-xl border px-4 py-3",
                        status === "rejected"
                          ? "border-danger/20 bg-danger-soft"
                          : "border-accent/20 bg-teal-soft",
                      )}
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Décision auditeur
                      </p>
                      <p className="mt-1 text-sm text-foreground">{data.validation_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {(hasOriginalPdf || hasSignedPdf) && (
                <div className="flex flex-wrap gap-2">
                  {hasOriginalPdf && (
                    <>
                      <button
                        type="button"
                        disabled={pdfBusy}
                        onClick={() => void handleOpenPdf(false)}
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-primary transition-colors hover:bg-card-muted disabled:opacity-60"
                      >
                        <FileText size={15} />
                        Ouvrir le proforma PDF
                      </button>
                      <button
                        type="button"
                        disabled={pdfBusy}
                        onClick={() => void handleDownloadPdf(false)}
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted disabled:opacity-60"
                      >
                        Télécharger PDF
                      </button>
                    </>
                  )}
                  {hasSignedPdf && (
                    <>
                      <button
                        type="button"
                        disabled={pdfBusy}
                        onClick={() => void handleOpenPdf(true)}
                        className="inline-flex items-center gap-2 rounded-lg border border-accent/30 bg-teal-soft px-3 py-2 text-sm text-accent transition-colors hover:bg-accent/10 disabled:opacity-60"
                      >
                        <FileText size={15} />
                        Ouvrir le PDF signé
                      </button>
                      <button
                        type="button"
                        disabled={pdfBusy}
                        onClick={() => void handleDownloadPdf(true)}
                        className="inline-flex items-center gap-2 rounded-lg border border-accent/30 bg-teal-soft px-3 py-2 text-sm text-accent transition-colors hover:bg-accent/10 disabled:opacity-60"
                      >
                        Télécharger PDF signé
                      </button>
                    </>
                  )}
                </div>
              )}

              {requestActs.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-foreground">
                    {canValidate ? "Actes demandés (validation auditeur)" : "Actes demandés"}
                  </h3>
                  <div className="overflow-auto rounded-xl border border-border">
                    <table className="safecheck-table w-full min-w-[680px]">
                      <thead className="bg-card-muted/60">
                        <tr className="border-b border-border">
                          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Acte
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Qté approuvée
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Prix unit.
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {requestActs.map((act) => {
                          const draft = actDrafts[act.id];
                          const qty = Number(draft?.quantity ?? act.quantity ?? 0);
                          const price = Number(draft?.unit_price ?? act.unit_price ?? 0);
                          return (
                            <tr key={act.id} className="border-b border-border last:border-0">
                              <td className="px-3 py-2 text-sm text-foreground">
                                <span className="font-semibold text-pink">{act.code}</span> — {act.label}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {canValidate ? (
                                  <input
                                    type="number"
                                    min={0}
                                    value={draft?.quantity ?? ""}
                                    onChange={(event) =>
                                      setActDrafts((prev) => ({
                                        ...prev,
                                        [act.id]: {
                                          quantity: event.target.value,
                                          unit_price: prev[act.id]?.unit_price ?? String(act.unit_price ?? 0),
                                          notes: prev[act.id]?.notes ?? "",
                                        },
                                      }))
                                    }
                                    className="h-8 w-20 rounded-md border border-border bg-card px-2 text-right text-sm"
                                  />
                                ) : (
                                  <span className="text-sm">{qty}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {canValidate ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={draft?.unit_price ?? ""}
                                    onChange={(event) =>
                                      setActDrafts((prev) => ({
                                        ...prev,
                                        [act.id]: {
                                          quantity: prev[act.id]?.quantity ?? String(act.quantity ?? 0),
                                          unit_price: event.target.value,
                                          notes: prev[act.id]?.notes ?? "",
                                        },
                                      }))
                                    }
                                    className="h-8 w-24 rounded-md border border-border bg-card px-2 text-right text-sm"
                                  />
                                ) : (
                                  <span className="text-sm">{formatAmount(price)}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right text-sm font-semibold">
                                {formatAmount(qty * price)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {canValidate && (
                    <p className="mt-2 text-sm font-semibold text-foreground">
                      Montant approuvé : {formatAmount(approvedTotal)}
                    </p>
                  )}
                </div>
              )}

              <div>
                <h3 className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
                  Actes couverts par le forfait
                  <StatusBadge variant="success">
                    {data.covered_acts_included ?? coveredActs.length} inclus
                  </StatusBadge>
                  {typeof data.covered_acts_total === "number" && (
                    <span className="text-xs font-normal text-muted">
                      sur {data.covered_acts_total} référencés
                    </span>
                  )}
                </h3>
                {data.covered_acts_truncated && (
                  <p className="mb-2 text-xs text-muted">
                    Affichage limité aux {coveredActs.length} premiers actes inclus.
                  </p>
                )}
                {coveredActs.length === 0 ? (
                  <p className="rounded-xl border border-border bg-card-muted/40 px-4 py-3 text-sm text-muted">
                    Aucun acte listé pour ce forfait.
                  </p>
                ) : (
                  <div className="overflow-auto rounded-xl border border-border">
                    <table className="safecheck-table w-full min-w-[560px]">
                      <thead className="bg-card-muted/60">
                        <tr className="border-b border-border">
                          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Code
                          </th>
                          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Libellé
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Coût
                          </th>
                          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                            Limite
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {coveredActs.map((act, index) => (
                          <tr
                            key={`${act.code}-${index}`}
                            className="border-b border-border last:border-0"
                          >
                            <td className="px-3 py-2 text-sm font-semibold text-pink">{act.code}</td>
                            <td className="px-3 py-2 text-sm uppercase text-foreground">
                              {act.label}
                            </td>
                            <td className="px-3 py-2 text-right text-sm text-foreground">
                              {act.cost !== null ? formatAmount(act.cost) : "—"}
                            </td>
                            <td className="px-3 py-2 text-sm text-muted">
                              {act.quantity_limit
                                ? `${act.quantity_limit}${act.time_period ? ` / ${act.time_period}` : ""}`
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
          {canValidate ? (
            <textarea
              value={validationNotes}
              onChange={(event) => setValidationNotes(event.target.value)}
              placeholder="Notes de validation (optionnel)"
              className="min-h-[40px] flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary"
            />
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {canValidate && (
              <>
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={() => void handleReject()}
                  className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-danger/90 disabled:opacity-60"
                >
                  Rejeter
                </button>
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={() => void handleApprove()}
                  className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent/90 disabled:opacity-60"
                >
                  Approuver
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-card-muted px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-border hover:text-foreground"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
